import { KindName } from 'graphql/hoopla/generated/graphql'
import { KindResponse } from 'state/useKinds'
import { KindExt } from 'types/hoopla'
import { View } from '../eventTypes'

const getView = (id?: string) => ({
  ARTIST_DETAILS: JSON.stringify({ name: 'ARTIST_DETAILS', id }),
  BORROWED: JSON.stringify({ name: 'BORROWED' }),
  BROWSE_ALL_KIND_COLLECTIONS: JSON.stringify({
    name: 'BROWSE_ALL_KIND_COLLECTIONS',
    id,
  }),
  BROWSE_BINGEPASS_CONTENT: JSON.stringify({
    name: 'BROWSE_BINGEPASS_CONTENT',
    id,
  }),
  BROWSE_COLLECTION: JSON.stringify({ name: 'BROWSE_COLLECTION', id }),
  BROWSE_COMIC_PUBLISHER: JSON.stringify({
    name: 'BROWSE_COMIC_PUBLISHER',
    id,
  }),
  BROWSE_COMIC_PUBLISHER_SERIES: JSON.stringify({
    name: 'BROWSE_COMIC_PUBLISHER_SERIES',
    id,
  }),
  BROWSE_GENRE: JSON.stringify({ name: 'BROWSE_GENRE', id }),
  BROWSE_IMPRINT_SERIES: JSON.stringify({ name: 'BROWSE_IMPRINT_SERIES', id }),
  BROWSE_IMPRINT: JSON.stringify({ name: 'BROWSE_IMPRINT', id }),
  BROWSE_KIND: JSON.stringify({ name: 'BROWSE_KIND', id }),
  BROWSE_KIND_CATEGORIES_SEARCH_RESULTS: JSON.stringify({
    name: 'BROWSE_KIND_CATEGORIES_SEARCH_RESULTS',
    id,
  }),
  BROWSE_KIND_CATEGORIES_SEARCH: JSON.stringify({
    name: 'BROWSE_KIND_CATEGORIES_SEARCH',
    id,
  }),
  BROWSE_KIND_COLLECTIONS: JSON.stringify({
    name: 'BROWSE_KIND_COLLECTIONS',
    id,
  }),
  BROWSE_KIND_FEATURED: JSON.stringify({ name: 'BROWSE_KIND_FEATURED', id }),
  BROWSE_KIND_GENRES: JSON.stringify({ name: 'BROWSE_KIND_GENRES', id }),
  BROWSE_KIND_JUST_FOR_YOU: JSON.stringify({
    name: 'BROWSE_KIND_JUST_FOR_YOU',
    id,
  }),
  BROWSE_KIND_POPULAR: JSON.stringify({ name: 'BROWSE_KIND_POPULAR', id }),
  BROWSE_KIND_PUBLISHERS: JSON.stringify({
    name: 'BROWSE_KIND_PUBLISHERS',
    id,
  }),
  BROWSE_KIND_RECENT: JSON.stringify({ name: 'BROWSE_KIND_RECENT', id }),
  BROWSE_KIND_RECOMMENDED: JSON.stringify({
    name: 'BROWSE_KIND_RECOMMENDED',
    id,
  }),
  BROWSE_KIND_SUBJECTS: JSON.stringify({ name: 'BROWSE_KIND_SUBJECTS', id }),
  BROWSE_KIND_TRENDING: JSON.stringify({ name: 'BROWSE_KIND_TRENDING', id }),
  BROWSE_LANGUAGE: JSON.stringify({ name: 'BROWSE_LANGUAGE', id }),
  BROWSE_NON_COMIC_PUBLISHER: JSON.stringify({
    name: 'BROWSE_NON_COMIC_PUBLISHER',
    id,
  }),
  BROWSE_SERIES: JSON.stringify({ name: 'BROWSE_SERIES', id }),
  BROWSE_SUBJECT: JSON.stringify({ name: 'BROWSE_SUBJECT', id }),
  BROWSE_TAG: JSON.stringify({ name: 'BROWSE_TAG', id }),
  CURATED_SEARCH_MANGA: JSON.stringify({ name: 'CURATED_SEARCH_MANGA' }),
  FAVORITE_ARTIST: JSON.stringify({ name: 'FAVORITE_ARTIST' }),
  FAVORITE_SERIES: JSON.stringify({ name: 'FAVORITE_SERIES' }),
  FAVORITE_KIND: JSON.stringify({ name: 'FAVORITE_KIND', id }),
  FAVORITES: JSON.stringify({ name: 'FAVORITES' }),
  HELP: JSON.stringify({ name: 'HELP' }),
  HISTORY: JSON.stringify({ name: 'HISTORY' }),
  HOLDS: JSON.stringify({ name: 'HOLDS' }),
  HOME: JSON.stringify({ name: 'HOME' }),
  LOGIN: JSON.stringify({ name: 'LOGIN' }),
  LOGIN_FORGOT_EMAIL: JSON.stringify({ name: 'LOGIN_FORGOT_EMAIL' }),
  REGISTRATION_WELCOME: JSON.stringify({ name: 'REGISTRATION_WELCOME' }),
  REGISTRATION_FIND_LIBRARY: JSON.stringify({
    name: 'REGISTRATION_FIND_LIBRARY',
  }),
  REGISTRATION_LIBRARY_INFO: JSON.stringify({
    name: 'REGISTRATION_LIBRARY_INFO',
  }),
  REGISTRATION_ACCOUNT_INFO: JSON.stringify({
    name: 'REGISTRATION_ACCOUNT_INFO',
  }),
  REGISTRATION_SUCCESS: JSON.stringify({ name: 'REGISTRATION_SUCCESS' }),
  REQUESTS: JSON.stringify({ name: 'REQUESTS' }),
  SEARCH: JSON.stringify({ name: 'SEARCH' }),
  SETTINGS_ACCOUNT: JSON.stringify({ name: 'SETTINGS_ACCOUNT' }),
  SETTINGS_EMAIL: JSON.stringify({ name: 'SETTINGS_EMAIL' }),
  SETTINGS_KIDS_MODE: JSON.stringify({ name: 'SETTINGS_KIDS_MODE' }),
  SETTINGS_LIBRARY: JSON.stringify({ name: 'SETTINGS_LIBRARY' }),
  SETTINGS_PASSWORD: JSON.stringify({ name: 'SETTINGS_PASSWORD' }),
  SETTINGS_RECOMMENDATIONS: JSON.stringify({
    name: 'SETTINGS_RECOMMENDATIONS',
  }),
  SUBMIT_FEEDBACK: JSON.stringify({ name: 'SUBMIT_FEEDBACK' }),
  TITLE_DETAILS: JSON.stringify({ name: 'TITLE_DETAILS', id }),
  WELCOME: JSON.stringify({ name: 'WELCOME' }),
})

const parsePathString = (path: string) => {
  // Remove leading slash if present
  const cleanPath = path.startsWith('/') ? path.substring(1) : path

  // Split the path by '/'
  const segments = cleanPath.split('/')
  const queryParams: Record<string, string> = {}
  const params = new URLSearchParams(window.location.search)
  params.forEach((value, key) => {
    queryParams[key] = value
  })
  return {
    segments: segments,
    section: segments[0] || '',
    category: segments[1] || '',
    fullPath: path,
    queryParams,
  }
}

type ViewerProps = {
  currentPageLoadedJSON: string | undefined
  currentView?: View
  getKindByName: (kindName: KindName) => KindResponse
  url: string
}

const viewer = ({
  currentPageLoadedJSON,
  currentView,
  getKindByName,
  url,
}: ViewerProps) => {
  let { category, fullPath, section, segments, queryParams } =
    parsePathString(url)
  const segmentIdentifier = segments[2]

  if (section === 'title' && segmentIdentifier === 'included-content') {
    return getView(segments[1]).BROWSE_BINGEPASS_CONTENT
  }

  if (section === 'home') {
    return getView().HOME
  }

  // if url starts with a kind, i.e. /ebook/categories/collections or /audiobook/{title}/{titleId}
  const kind = getKindByName(section.toUpperCase() as KindName)
  if (!!kind) {
    if (category === 'categories') {
      switch (segmentIdentifier) {
        case 'collections':
          if (fullPath.includes('PUBLIC')) {
            return getView(kind.id).BROWSE_ALL_KIND_COLLECTIONS
          }
          return getView(kind.id).BROWSE_KIND_COLLECTIONS
        case 'genres':
          return getView(kind.id).BROWSE_KIND_GENRES
        case 'publishers':
          return getView(kind.id).BROWSE_KIND_PUBLISHERS
        case 'search': {
          const searchParams = new URLSearchParams(window.location.search)
          if (searchParams.size > 0) {
            return getView(kind.id).BROWSE_KIND_CATEGORIES_SEARCH_RESULTS
          }
          return getView(kind.id).BROWSE_KIND_CATEGORIES_SEARCH
        }
        case 'subjects':
          return getView(kind.id).BROWSE_KIND_SUBJECTS
      }
    }

    if (category === 'genre') {
      return getView(kind.id).BROWSE_GENRE
    }

    if (category === 'subject') {
      return getView(segmentIdentifier).BROWSE_SUBJECT
    }

    // handle nested comic imprint/series pages
    if (section === 'comic') {
      const isPublisher = segments[1] === 'publisher'
      const isImprint = segments[3] === 'imprint'
      const imprintId = segments[5]
      const isSeries = segments[4] === 'series' || segments[6] === 'series'

      if (isImprint) {
        if (isSeries) {
          return getView(imprintId).BROWSE_IMPRINT_SERIES
        }
        return getView(imprintId).BROWSE_IMPRINT
      }

      if (isPublisher) {
        const publisherId = segments[3]
        if (isSeries) {
          return getView(publisherId).BROWSE_COMIC_PUBLISHER_SERIES
        }
        return getView(publisherId).BROWSE_COMIC_PUBLISHER
      }
    }

    // If there is no other kind page, then we are on the title detail page
    return getView(segmentIdentifier).TITLE_DETAILS
  }

  if (fullPath === '/') {
    return getView().WELCOME
  }

  if (section === 'artist') {
    return getView(segmentIdentifier).ARTIST_DETAILS
  }

  if (section === 'browse') {
    const correctSEOPath = category === 'binge'
    if (correctSEOPath) {
      category = 'bingePass'
    }
    const kind = getKindByName(category.toUpperCase() as KindName) as KindExt

    switch (segmentIdentifier) {
      case 'featured':
        return getView(kind.id).BROWSE_KIND_FEATURED
      case 'justForYou':
        return getView(kind.id).BROWSE_KIND_JUST_FOR_YOU
      case 'popular':
        return getView(kind.id).BROWSE_KIND_POPULAR
      case 'recommended':
        return getView(kind.id).BROWSE_KIND_RECOMMENDED
      case 'publishers':
        return getView(kind.id).BROWSE_KIND_PUBLISHERS
    }

    if (!!kind?.id) {
      return getView(kind.id).BROWSE_KIND
    }
  }

  if (section === 'collection') {
    return getView(segmentIdentifier).BROWSE_COLLECTION
  }

  if (section === 'cs') {
    if (category === 'manga') {
      return getView().CURATED_SEARCH_MANGA
    }
  }

  if (section === 'faq') {
    return getView().HELP
  }

  if (section === 'feedback') {
    return getView().SUBMIT_FEEDBACK
  }

  if (section === 'language') {
    return getView(segments[1]).BROWSE_LANGUAGE
  }

  if (section === 'register') {
    switch (queryParams.page) {
      case 'REGISTRATION_WELCOME':
        return getView().REGISTRATION_WELCOME
      case 'REGISTRATION_FIND_LIBRARY':
        return getView().REGISTRATION_FIND_LIBRARY
      case 'REGISTRATION_LIBRARY_INFO':
        return getView().REGISTRATION_LIBRARY_INFO
      case 'REGISTRATION_ACCOUNT_INFO':
        return getView().REGISTRATION_ACCOUNT_INFO
      case 'REGISTRATION_SUCCESS':
        return getView().REGISTRATION_SUCCESS
    }
  }

  if (section === 'login') {
    switch (queryParams.page) {
      case 'LOGIN':
        return getView().LOGIN
      case 'LOGIN_FORGOT_EMAIL':
        return getView().LOGIN_FORGOT_EMAIL
    }
  }

  if (section === 'my') {
    switch (category) {
      case 'borrowed':
        return getView().BORROWED
      case 'favorites':
        if (segmentIdentifier === 'artist') {
          return getView().FAVORITE_ARTIST
        }
        if (segmentIdentifier === 'series') {
          return getView().FAVORITE_SERIES
        }
        if (segmentIdentifier === 'title') {
          return getView(segments[3]).FAVORITE_KIND
        }
        // default
        return getView().FAVORITES
      case 'history':
        return getView().HISTORY
      case 'holds':
        return getView().HOLDS
      case 'hoopla':
        return getView().HOME
      case 'requests':
        return getView().REQUESTS
      case 'settings':
        switch (segmentIdentifier) {
          case 'account':
            return getView().SETTINGS_ACCOUNT
          case 'email':
            return getView().SETTINGS_EMAIL
          case 'kids-mode':
            return getView().SETTINGS_KIDS_MODE
          case 'library':
            return getView().SETTINGS_LIBRARY
          case 'password':
            return getView().SETTINGS_PASSWORD
          case 'recommendations':
            return getView().SETTINGS_RECOMMENDATIONS
        }
    }
  }
  if (section === 'publisher') {
    return getView(segmentIdentifier).BROWSE_NON_COMIC_PUBLISHER
  }

  if (section === 'search') {
    return getView().SEARCH
  }

  if (section === 'series') {
    return getView(segmentIdentifier).BROWSE_SERIES
  }

  if (section === 'tag') {
    return getView(segments[1]).BROWSE_TAG
  }

  if (section === 'trending') {
    const searchParams = new URLSearchParams(window.location.search)
    const kindName = searchParams.get('kindName')
    const kind = getKindByName(kindName as KindName) as KindExt
    return getView(kind?.id).BROWSE_KIND_TRENDING
  }

  return currentView ? JSON.stringify(currentView) : currentPageLoadedJSON
}

export default viewer
